import { lazy, Suspense, PropsWithChildren, ComponentProps } from 'react';
import { isLazy } from 'react-is';
import {
    createBrowserRouter,
    createRoutesFromElements,
    Navigate,
    Route,
    RouteObject,
    useLocation,
} from 'react-router';
import { RouterProvider } from 'react-router/dom';
import Layout from 'components/Layout';
import { Loader } from 'components/Loader/Loader';
import { LoadAccount } from 'components/LoadAccount/LoadAccount';
import { AuthCallbackLoader } from 'components/AuthCallbackLoader/AuthCallbackLoader';
import { SiteRoute } from 'components/SiteRoute/SiteRoute';
import { PrivateRoute } from 'components/PrivateRoute/PrivateRoute';
import {
    LegacyNewRedirect,
    LegacyHashRedirect,
} from 'components/LegacyRedirect/LegacyRedirect';
import Logout from 'pages/Logout';
import NotFound from 'pages/NotFound';
import { ErrorHandler } from 'pages/ErrorHandler/ErrorHandler';
import { DrawerProvider } from 'utils/contexts/DrawerContext/DrawerContext';
import { MetadataProvider } from 'utils/contexts/MetadataContext';
import { AuthProvider } from 'utils/contexts/AuthContext';
import { HintProvider } from 'utils/contexts/HintContext';
import { SiteProvider } from 'utils/contexts/SiteContext';
import { ToastProvider } from 'utils/contexts/ToastContext';
import { ModalProvider } from 'utils/contexts/ModalContext';

// prettier-ignore
const AddSite = lazy(() => import(/* webpackChunkName: "AddSite" */ 'pages/Sites/AddSite'));
// prettier-ignore
const Sites = lazy(() => import(/* webpackChunkName: "SitesPage" */ 'pages/Sites'));
// prettier-ignore
const OrderSite = lazy(() => import(/* webpackChunkName: "OrderSite" */ 'pages/Sites/OrderSite'));
// prettier-ignore
const Alerts = lazy(() => import(/* webpackChunkName: "AlertsPage" */ 'pages/Alerts'));
// prettier-ignore
const Login = lazy(() => import(/* webpackChunkName: "LoginPage" */ 'pages/Login'));
// prettier-ignore
const Site = lazy(() => import(/* webpackChunkName: "Site" */ 'pages/Site'));
// prettier-ignore
const Overview = lazy(() => import(/* webpackChunkName: "OverviewPage" */ 'pages/Overview'));
// prettier-ignore
const Diagnostics = lazy(() => import(/* webpackChunkName: "DiagnosticsPage" */ 'pages/Diagnostics'));
// prettier-ignore
const DiagnosticsTests = lazy(() => import(/* webpackChunkName: "DiagnosticsTests" */ 'pages/Diagnostics/subpages/Tests'));
// prettier-ignore
const DiagnosticsTestDrawer = lazy(() => import(/* webpackChunkName: "DiagnosticsTestDrawer" */ 'pages/Diagnostics/subpages/Tests/drawer'));
// prettier-ignore
const DiagnosticsTestResults = lazy(() => import(/* webpackChunkName: "DiagnosticsTestResults" */ 'pages/Diagnostics/subpages/TestResults'));
// prettier-ignore
const DiagnosticsTestResult = lazy(() => import(/* webpackChunkName: "DiagnosticsTestResult" */ 'pages/Diagnostics/subpages/TestResults/details/TestResult'));
// prettier-ignore
const Performance = lazy(() => import(/* webpackChunkName: "PerformancePage" */ 'pages/Performance'));
// prettier-ignore
const Configuration = lazy(() => import(/* webpackChunkName: "ConfigurationPage" */ 'pages/Configuration'));
// prettier-ignore
const ConfigurationGeneral = lazy(() => import(/* webpackChunkName: "ConfigurationGeneral" */ 'pages/Configuration/tabs/General'));
// prettier-ignore
const ConfigurationCircuits = lazy(() => import(/* webpackChunkName: "ConfigurationCircuits" */ 'pages/Configuration/tabs/Circuits'));
// prettier-ignore
const ConfigurationDevices = lazy(() => import(/* webpackChunkName: "ConfigurationDevices" */ 'pages/Configuration/tabs/Devices'));
// prettier-ignore
const ConfigurationLanSettings = lazy(
    () =>
        import(
            /* webpackChunkName: "ConfigurationLanSettings" */ 'pages/Configuration/tabs/LanSettings'
        )
);
// prettier-ignore
const ConfigurationLanClients = lazy(() => import(/* webpackChunkName: "ConfigurationLan" */ 'pages/Configuration/tabs/LanClients'));
// prettier-ignore
const ConfigurationWiFi = lazy(() => import(/* webpackChunkName: "ConfigurationWiFi" */ 'pages/Configuration/tabs/WiFi'));
// prettier-ignore
const ConfigurationVPN = lazy(
    () =>
        import(
            /* webpackChunkName: "ConfigurationVPN" */ 'pages/Configuration/tabs/VPN'
        )
);
const ConfigurationFirewall = lazy(
    () =>
        import(
            /* webpackChunkName: "ConfigurationFirewall" */ 'pages/Configuration/tabs/Firewall'
        )
);
// prettier-ignore
const ConfigurationRules = lazy(() => import(/* webpackChunkName: "ConfigurationRules" */ 'pages/Configuration/tabs/Rules'));
// prettier-ignore
const Settings = lazy(() => import(/* webpackChunkName: "SettingsPage" */ 'pages/Settings'));
// prettier-ignore
const Logs = lazy(() => import(/* webpackChunkName: "LogsPage" */ 'pages/Logs'));
const GeneralLogs = lazy(() => import('pages/Logs/tabs/General'));
const FirewallLogs = lazy(() => import('pages/Logs/tabs/Firewall'));
const VpnLogs = lazy(() => import('pages/Logs/tabs/VPN'));
// prettier-ignore
const Companies = lazy(() => import(/* webpackChunkName: "CompaniesPage" */ 'pages/Companies'));
// prettier-ignore
const Company = lazy(() => import(/* webpackChunkName: "CompanyPage" */ 'pages/Company'));
// prettier-ignore
const CompanyOptions = lazy(() => import(/* webpackChunkName: "CompanyOptions" */ 'pages/Company/tabs/Options'));
// prettier-ignore
const CompanyChildren = lazy(() => import(/* webpackChunkName: "CompanyChildren" */ 'pages/Company/tabs/Children'));
// prettier-ignore
const CompanyDevices = lazy(() => import(/* webpackChunkName: "CompanyDevices" */ 'pages/Company/tabs/Devices'));
// prettier-ignore
const CompanyUsers = lazy(() => import(/* webpackChunkName: "CompanyUsers" */ 'pages/Company/tabs/Users'));
// prettier-ignore
const CompanyUserSettings = lazy(() => import(/* webpackChunkName: "CompanyUserSettings" */ 'pages/Company/tabs/UserSettings'));
// prettier-ignore
const CompanyAlertSettings = lazy(() => import(/* webpackChunkName: "CompanyAlertSettings" */ 'pages/Company/tabs/AlertSettings'));
// prettier-ignore
const CompanyAlertSettingsAllAlerts = lazy(() => import(/* webpackChunkName: "CompanyAlertSettingsAllAlerts" */ 'pages/Company/tabs/AlertSettings/tabs/AllAlerts'));
// prettier-ignore
const CompanyAlertSettingsCustomRules = lazy(() => import(/* webpackChunkName: "CompanyAlertSettingsCustomRules" */ 'pages/Company/tabs/AlertSettings/tabs/CustomRules'));
// prettier-ignore
const CompanyAlertSettingsCustomRuleWizardForm = lazy(() => import(/* webpackChunkName: "CompanyAlertSettingsCustomRuleWizardForm */ 'pages/Company/tabs/AlertSettings/components/CustomRuleWizardForm'));
// prettier-ignore
const AddCompany = lazy(() => import(/* webpackChunkName: "CompanyOptions" */ 'pages/Company/AddCompany'));
// prettier-ignore
const Campaign = lazy(() => import(/* webpackChunkName: "Campaign" */ 'pages/Campaign'));
// prettier-ignore
const SiteLogs = lazy(() => import(/* webpackChunkName: "SiteLogs" */ 'pages/Sites/tabs/Logs'));
// prettier-ignore
const CompanyLogs = lazy(() => import(/* webpackChunkName: "CompanyLogs" */ 'pages/Company/tabs/Logs'));
// prettier-ignore
const CompanySites = lazy(() => import(/* webpackChunkName: "CompanyLogs" */ 'pages/Company/tabs/Sites'));

const Admin = lazy(() => import('pages/Admin'));
const AdminMessages = lazy(() => import('pages/Admin/subpages/Messages'));

const Mfa = lazy(() => import('pages/Mfa'));

const ContextWrapper = ({ children }: PropsWithChildren) => (
    <MetadataProvider>
        <AuthProvider>
            <SiteProvider>
                <HintProvider>
                    <DrawerProvider>
                        <ToastProvider>
                            <ModalProvider>{children}</ModalProvider>
                        </ToastProvider>
                    </DrawerProvider>
                </HintProvider>
            </SiteProvider>
        </AuthProvider>
    </MetadataProvider>
);

const router = createBrowserRouter(
    createRoutesFromElements(
        <Route
            element={
                <ContextWrapper>
                    <LegacyHashRedirect />
                    <LoadAccount />
                </ContextWrapper>
            }
            errorElement={<ErrorHandler />}
        >
            <Route path="oauth-callback" loader={AuthCallbackLoader} />
            <Route path="mfa" element={<Mfa />} />
            <Route path="login" element={<Login />} />
            <Route path="logout" element={<Logout />} />
            {/* Redirect legacy /new/* paths to root /* */}
            <Route path="new/*" element={<LegacyNewRedirect />} />
            <Route path="new" element={<LegacyNewRedirect />} />
            <Route element={<PrivateRoute />}>
                <Route element={<Layout />}>
                    <Route
                        path="*"
                        element={<NotFound />}
                        handle={{ title: 'not_found' }}
                    />
                    {/* non-site specific pages */}
                    <Route index element={<Navigate replace to="/sites" />} />
                    <Route
                        path="settings"
                        element={<Settings />}
                        handle={{
                            zendeskHorizPosition: 'left',
                            helpUrl:
                                'https://support.bigleaf.net/hc/en-us/articles/25706442532763-User-Settings',
                            title: 'user_settings',
                        }}
                    />

                    {/* logs pages */}
                    <Route
                        path="logs"
                        element={<Logs />}
                        handle={{
                            helpUrl:
                                'https://support.bigleaf.net/hc/en-us/articles/26264380252699-Logs',
                            title: 'logs',
                        }}
                    >
                        <Route index element={<GeneralLogs />} />
                        <Route path="firewall" element={<FirewallLogs />} />
                        <Route path="vpn" element={<VpnLogs />} />
                    </Route>

                    {/* companies pages */}
                    <Route
                        path="companies"
                        handle={{
                            helpUrl:
                                'https://support.bigleaf.net/hc/en-us/articles/27479666845083-Companies',
                        }}
                    >
                        <Route
                            index
                            element={<Companies />}
                            handle={{
                                title: 'companies',
                            }}
                        />
                        <Route
                            path="create"
                            element={<AddCompany />}
                            handle={{
                                zendeskHorizPosition: 'left',
                                title: 'add_company',
                            }}
                        />
                        <Route
                            path=":companyId"
                            element={<Company />}
                            handle={{
                                helpUrl:
                                    'https://support.bigleaf.net/hc/en-us/articles/27639209737883-Company-Settings',
                                title: 'company.options',
                            }}
                        >
                            <Route
                                index
                                element={
                                    <Navigate to="options" replace={true} />
                                }
                            />
                            <Route
                                path="options"
                                element={<CompanyOptions />}
                                handle={{
                                    zendeskHorizPosition: 'left',
                                }}
                            />
                            <Route
                                path="children"
                                element={<CompanyChildren />}
                                handle={{ title: 'company.children' }}
                            />
                            <Route
                                path="devices"
                                element={<CompanyDevices />}
                                handle={{ title: 'company.devices' }}
                            />
                            <Route
                                path="users"
                                element={<CompanyUsers />}
                                handle={{ title: 'company.users' }}
                            />
                            <Route
                                path="users/:account_id/settings"
                                element={<CompanyUserSettings />}
                                handle={{
                                    zendeskHorizPosition: 'left',
                                    title: 'company.user_settings',
                                }}
                            />
                            <Route
                                path="alert-settings"
                                element={<CompanyAlertSettings />}
                                handle={{
                                    title: 'company.alert_settings',
                                }}
                            >
                                <Route
                                    index
                                    element={
                                        <Navigate to="all-alerts" replace />
                                    }
                                />
                                <Route
                                    path="all-alerts"
                                    element={<CompanyAlertSettingsAllAlerts />}
                                >
                                    <Route
                                        path=":riskDescriptionId"
                                        element={
                                            <Suspense fallback={null}>
                                                <CompanyAlertSettingsCustomRuleWizardForm />
                                            </Suspense>
                                        }
                                    />
                                </Route>
                                <Route
                                    path="custom-rules"
                                    element={
                                        <CompanyAlertSettingsCustomRules />
                                    }
                                >
                                    <Route
                                        path=":overrideRuleId"
                                        element={
                                            <Suspense fallback={null}>
                                                <CompanyAlertSettingsCustomRuleWizardForm />
                                            </Suspense>
                                        }
                                    />
                                </Route>
                            </Route>

                            <Route
                                path="logs"
                                element={<CompanyLogs />}
                                handle={{
                                    helpUrl:
                                        'https://support.bigleaf.net/hc/en-us/articles/26264380252699-Logs',
                                    title: 'company.logs',
                                }}
                            >
                                <Route index element={<GeneralLogs />} />
                                <Route
                                    path="firewall"
                                    element={<FirewallLogs />}
                                />
                                <Route path="vpn" element={<VpnLogs />} />
                            </Route>

                            <Route
                                path="sites"
                                element={<CompanySites />}
                                handle={{
                                    title: 'company.sites',
                                }}
                            />
                        </Route>
                    </Route>
                    {/* alerts pages */}
                    <Route
                        path="alerts"
                        handle={{
                            helpUrl:
                                'https://support.bigleaf.net/hc/en-us/articles/27479711318939-Alerts',
                            title: 'alerts',
                        }}
                    >
                        <Route
                            index
                            element={<Navigate to="active" replace />}
                            handle={{ title: 'alerts.active' }}
                        />
                        <Route path=":type" element={<Alerts />} />
                    </Route>

                    <Route
                        path="sites/create"
                        element={<AddSite />}
                        handle={{
                            zendeskHorizPosition: 'left',
                            title: 'provision_new_site',
                        }}
                    />

                    <Route
                        path="sites/order"
                        element={<OrderSite />}
                        handle={{ title: 'order_new_site' }}
                    />

                    <Route
                        path="sites/:type?"
                        element={<Sites />}
                        handle={{
                            helpUrl:
                                'https://support.bigleaf.net/hc/en-us/articles/27037325100059-Sites',
                            title: 'sites',
                        }}
                    />

                    {/* site specific pages */}
                    <Route path="site/:siteId" element={<SiteRoute />}>
                        <Route element={<Site />}>
                            <Route path="dashboard">
                                <Route
                                    index
                                    element={<Navigate to="overview" replace />}
                                />
                                <Route
                                    path="overview"
                                    handle={{
                                        helpUrl:
                                            'https://support.bigleaf.net/hc/en-us/articles/25523872950811-Site-Overview',
                                        title: 'site.overview',
                                    }}
                                >
                                    <Route index element={<Overview />} />
                                    <Route
                                        path=":widget"
                                        element={<Overview />}
                                    />
                                </Route>
                                <Route
                                    path="diagnostics"
                                    element={<Diagnostics />}
                                    handle={{
                                        helpUrl:
                                            'https://support.bigleaf.net/hc/en-us/articles/25524368352155-Diagnostics-Page',
                                        title: 'site.diagnostics',
                                    }}
                                >
                                    <Route
                                        index
                                        element={
                                            <Navigate to="tests" replace />
                                        }
                                    />
                                    <Route
                                        path="tests"
                                        element={<DiagnosticsTests />}
                                    >
                                        <Route
                                            path=":testId"
                                            element={
                                                <Suspense fallback={null}>
                                                    <DiagnosticsTestDrawer />
                                                </Suspense>
                                            }
                                        />
                                    </Route>
                                    <Route
                                        path="test-results"
                                        element={<DiagnosticsTestResults />}
                                    />
                                </Route>
                                {/*
                                 * Detailed test result page requires
                                 * a separate `Route` because it should not
                                 * inherit layout from its "parent" routes.
                                 */}
                                <Route
                                    path="diagnostics/test-results/:testResultId"
                                    element={<DiagnosticsTestResult />}
                                    handle={{
                                        helpUrl:
                                            'https://support.bigleaf.net/hc/en-us/articles/25524368352155-Diagnostics-Page',
                                    }}
                                />

                                <Route
                                    path="performance"
                                    element={<Performance />}
                                    handle={{
                                        helpUrl:
                                            'https://support.bigleaf.net/hc/en-us/articles/25550933495323-Performance',
                                        title: 'site.performance',
                                    }}
                                />

                                <Route
                                    path="logs"
                                    element={<SiteLogs />}
                                    handle={{
                                        helpUrl:
                                            'https://support.bigleaf.net/hc/en-us/articles/26264380252699-Logs',
                                        title: 'site.logs',
                                    }}
                                >
                                    <Route index element={<GeneralLogs />} />
                                    <Route
                                        path="firewall"
                                        element={<FirewallLogs />}
                                    />
                                    <Route path="vpn" element={<VpnLogs />} />
                                </Route>
                            </Route>

                            <Route
                                path="configuration/:mode?"
                                element={<Configuration />}
                                handle={{
                                    helpUrl:
                                        'https://support.bigleaf.net/hc/en-us/articles/26139675144987-Site-Configuration',
                                    zendeskHorizPosition: 'left',
                                    title: 'site_config.general',
                                }}
                            >
                                <Route
                                    index
                                    element={<Navigate to="general" replace />}
                                />
                                <Route
                                    path="general"
                                    element={<ConfigurationGeneral />}
                                />
                                <Route
                                    path="circuits"
                                    element={<ConfigurationCircuits />}
                                    handle={{
                                        title: 'site_config.circuits',
                                    }}
                                />
                                <Route
                                    path="devices"
                                    element={<ConfigurationDevices />}
                                    handle={{
                                        title: 'site_config.devices',
                                    }}
                                />
                                <Route
                                    path="lan_settings"
                                    element={<ConfigurationLanSettings />}
                                    handle={{
                                        title: 'site_config.lan_settings',
                                    }}
                                />
                                <Route
                                    path="lan_clients"
                                    element={<ConfigurationLanClients />}
                                />
                                <Route
                                    path="wifi"
                                    element={<ConfigurationWiFi />}
                                    handle={{ title: 'site_config.wifi' }}
                                />
                                <Route
                                    path="custom_qos"
                                    element={<ConfigurationRules />}
                                    handle={{ title: 'site_config.rules' }}
                                />
                                <Route
                                    path="vpn"
                                    element={<ConfigurationVPN />}
                                    handle={{ title: 'site_config.vpn' }}
                                />
                                <Route
                                    path="firewall"
                                    element={<ConfigurationFirewall />}
                                    handle={{ title: 'site_config.firewall' }}
                                />
                            </Route>
                        </Route>
                    </Route>

                    <Route path="admin" element={<Admin />}>
                        <Route
                            index
                            element={<Navigate to="messages" replace />}
                        />
                        <Route
                            path="messages"
                            element={<AdminMessages />}
                            handle={{ title: 'admin.messages' }}
                        />
                    </Route>
                </Route>
            </Route>

            <Route path="campaign/:id" element={<Campaign />} />
        </Route>
    ).map(function wrapWithSuspense<T extends RouteObject>(routeObject: T): T {
        return {
            ...routeObject,
            ...(routeObject.element
                ? {
                      element:
                          // Wrap all elements of type `lazy` with `Suspense`.
                          isLazy(routeObject.element) ? (
                              <Suspense
                                  /*
                                   * `key` may seem unnecessary here, but it is
                                   * in fact required to show `Loader` during
                                   * code lazy loading.
                                   * Source: https://react.dev/reference/react/Suspense#resetting-suspense-boundaries-on-navigation
                                   */
                                  key={routeObject.path}
                                  fallback={<Loader />}
                              >
                                  {routeObject.element}
                              </Suspense>
                          ) : (
                              routeObject.element
                          ),
                  }
                : undefined),
            ...(routeObject.children
                ? { children: routeObject.children.map(wrapWithSuspense) }
                : undefined),
        };
    })
);

const App = () => <RouterProvider router={router} />;

export default App;
